import {
  MGD003_BATTERY_COLUMN_MIN_KWH,
  MGD003_BATTERY_COLUMNS,
  MGD003_CONSUMPTION_BANDS_KWH,
  MGD003_GENERATION_MAX_KWH,
  MGD003_GENERATION_STEP_KWH,
  MGD003_TABLES,
  type Mgd003Archetype,
} from './solarMgd003Tables.generated';

/**
 * MCS MGD 003 (Issue 2.0, 1 April 2022): the self-consumption estimate for a
 * domestic solar PV system, with and without a battery, from the section 7
 * lookup tables.
 *
 * Inputs and their defaults come from the guidance itself: the occupancy
 * archetype is asked of the occupier and "in half the day" stands in when
 * it is not known (Table 4-1); annual consumption comes from twelve months
 * of bills and 3,500 kWh stands in when none are available (Figure 4-1);
 * self-consumption is capped at 95% of generation (4.6); grid independence
 * is self-consumption over consumption (4.7), capped at 90% of what the
 * property uses (4.7.3). The tables run to 6,000 kWh of generation a year
 * (3.11); a system that generates more is read at the top generation band,
 * the same way an out-of-range consumption or battery capacity is, so
 * MIS 3002 4.1.2 still gets a figure and the document says where it was read.
 */

export type { Mgd003Archetype } from './solarMgd003Tables.generated';
/** The generation the tables run to, kWh a year (3.11). */
export { MGD003_GENERATION_MAX_KWH } from './solarMgd003Tables.generated';

export const MGD003_DEFAULT_ARCHETYPE: Mgd003Archetype = 'IN_HALF_THE_DAY';
export const MGD003_DEFAULT_CONSUMPTION_KWH = 3500;
export const MGD003_SELF_CONSUMPTION_CAP = 0.95;
/** 4.7.3: grid independence is capped at 90% "to prevent irregular results". */
export const MGD003_GRID_INDEPENDENCE_CAP = 0.9;
/** Largest usable storage capacity the section 7 columns cover, kWh. */
export const MGD003_BATTERY_MAX_KWH =
  MGD003_BATTERY_COLUMN_MIN_KWH + MGD003_BATTERY_COLUMNS;
/** The consumption the tables run from and to, kWh a year (3.11, Figure 4-1). */
export const MGD003_CONSUMPTION_MIN_KWH = MGD003_CONSUMPTION_BANDS_KWH[0];
export const MGD003_CONSUMPTION_MAX_KWH =
  MGD003_CONSUMPTION_BANDS_KWH[MGD003_CONSUMPTION_BANDS_KWH.length - 1] + 500;

export const MGD003_ARCHETYPE_LABELS: Record<Mgd003Archetype, string> = {
  HOME_ALL_DAY: 'Home all day',
  IN_HALF_THE_DAY: 'In half the day',
  OUT_DURING_THE_DAY: 'Out all day',
};

export type SelfConsumptionInput = {
  occupancyArchetype: Mgd003Archetype | null;
  /** From bills; null when not known. */
  annualConsumptionKwh: number | null;
  /** The MCS standard estimate for the system, kWh. */
  annualGenerationKwh: number;
  /** Usable battery capacity on the estimate, kWh; null or 0 without one. */
  batteryUsableKwh: number | null;
};

export type SelfConsumptionFigures = {
  /** Table percentage, after the 95% cap. */
  percent: number;
  selfConsumedKwh: number;
  /** 4.7: self-consumption over annual consumption, capped at 90% (4.7.3). */
  selfSufficiencyPercent: number;
  /**
   * The same figure as an absolute kWh value, which Table 5-1 asks for
   * alongside the percentage.
   */
  selfSufficiencyKwh: number;
};

export type SelfConsumptionEstimate = {
  archetype: Mgd003Archetype;
  archetypeAssumed: boolean;
  consumptionKwh: number;
  consumptionAssumed: boolean;
  /** True when consumption fell outside the tables and the nearest band was used. */
  consumptionClamped: boolean;
  /** The consumption band the table was read at, kWh. */
  consumptionBandKwh: number;
  generationKwh: number;
  /**
   * True when generation ran past the top of the tables, so the figures were
   * read at the top of the range instead. `generationKwh` stays the system's
   * own figure; the document says where the numbers came from.
   */
  generationClamped: boolean;
  /** The generation the table was read at, kWh. */
  generationBandKwh: number;
  pvOnly: SelfConsumptionFigures;
  withBattery:
    | (SelfConsumptionFigures & {
        usableKwh: number;
        capacityClamped: boolean;
      })
    | null;
};

const consumptionBand = (kwh: number): { band: number; clamped: boolean } => {
  const first = MGD003_CONSUMPTION_BANDS_KWH[0];
  const last =
    MGD003_CONSUMPTION_BANDS_KWH[MGD003_CONSUMPTION_BANDS_KWH.length - 1];
  if (kwh < first) return { band: first, clamped: true };
  if (kwh >= last + 500) return { band: last, clamped: true };
  return { band: Math.floor(kwh / 500) * 500, clamped: false };
};

const batteryColumn = (
  usableKwh: number
): { column: number; clamped: boolean } | null => {
  if (usableKwh < MGD003_BATTERY_COLUMN_MIN_KWH) return null;
  const column = Math.floor(usableKwh - (MGD003_BATTERY_COLUMN_MIN_KWH - 1));
  if (column > MGD003_BATTERY_COLUMNS)
    return { column: MGD003_BATTERY_COLUMNS, clamped: true };
  return { column, clamped: false };
};

const figures = (
  percentFromTable: number,
  generationKwh: number,
  consumptionKwh: number
): SelfConsumptionFigures => {
  const fraction = Math.min(
    percentFromTable / 100,
    MGD003_SELF_CONSUMPTION_CAP
  );
  const selfConsumedKwh = Math.round(generationKwh * fraction);
  // 4.7.3 caps grid independence at 90% of what the property uses, so a
  // battery on a generous roof cannot print more than 100% self-sufficient.
  const independenceFraction =
    consumptionKwh > 0
      ? Math.min(selfConsumedKwh / consumptionKwh, MGD003_GRID_INDEPENDENCE_CAP)
      : 0;
  return {
    percent: Math.round(fraction * 100),
    selfConsumedKwh,
    selfSufficiencyPercent: Math.round(independenceFraction * 100),
    selfSufficiencyKwh: Math.round(independenceFraction * consumptionKwh),
  };
};

export const estimateSelfConsumption = (
  input: SelfConsumptionInput
): SelfConsumptionEstimate => {
  const generationKwh = Math.round(input.annualGenerationKwh);
  const archetype = input.occupancyArchetype ?? MGD003_DEFAULT_ARCHETYPE;
  const consumptionKwh =
    input.annualConsumptionKwh ?? MGD003_DEFAULT_CONSUMPTION_KWH;
  const { band, clamped } = consumptionBand(consumptionKwh);
  const grid = MGD003_TABLES[archetype][band];
  // The tables stop at 6,000 kWh of generation, so a bigger system reads the
  // top band. Its percentage is read at the top of the range too, not
  // applied to the system's own generation: the top band's share of a
  // 10,000 kWh array would print more self-consumption than the home uses.
  const generationClamped = generationKwh >= MGD003_GENERATION_MAX_KWH;
  const bandGenerationKwh = generationClamped
    ? MGD003_GENERATION_MAX_KWH
    : generationKwh;
  const row =
    grid[
      Math.min(
        grid.length - 1,
        Math.floor(generationKwh / MGD003_GENERATION_STEP_KWH)
      )
    ];

  const pvOnly = figures(row[0], bandGenerationKwh, consumptionKwh);
  const battery =
    input.batteryUsableKwh != null && input.batteryUsableKwh > 0
      ? batteryColumn(input.batteryUsableKwh)
      : null;
  const withBattery =
    battery && input.batteryUsableKwh != null
      ? {
          ...figures(row[battery.column], bandGenerationKwh, consumptionKwh),
          usableKwh: input.batteryUsableKwh,
          capacityClamped: battery.clamped,
        }
      : null;

  return {
    archetype,
    archetypeAssumed: input.occupancyArchetype == null,
    consumptionKwh,
    consumptionAssumed: input.annualConsumptionKwh == null,
    consumptionClamped: clamped,
    consumptionBandKwh: band,
    generationKwh,
    generationClamped,
    generationBandKwh: bandGenerationKwh,
    pvOnly,
    withBattery,
  };
};
